import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Interfaz para acciones del historial de un servicio
 */
export interface ServiceHistoryAction {
  id: number;
  date: string;
  action: string;
  user: string;
  ip: string;
  device: string;
  browser: string;
  location: string;
}

/**
 * Página de historial de acciones de un servicio
 */
export interface ServiceHistoryPageResponse {
  items: ServiceHistoryAction[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

@Injectable({ providedIn: 'root' })
export class ServiceHistoryService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/services`;
  }

  /**
   * Obtiene el historial de acciones de un servicio (paginación server-side).
   *
   * Endpoint: GET /api/settings/services/{id}/history?page=&size=&sort=&direction=&search=
   */
  getHistory(
    id: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
  ): Observable<ServiceHistoryPageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }

    return this.httpClient
      .get<ApiResponse<PageableData>>(`${this.apiUrl}/${id}/history`, {
        params,
      })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const rawItems = pageableData?.content || [];
          const items: ServiceHistoryAction[] = rawItems.map((action: any) => ({
            id: action.id,
            date: action.date,
            action: action.action || 'Acción',
            user: action.user || 'Usuario',
            ip: action.ip || '',
            device: action.device || '',
            browser: action.browser || '',
            location: action.location || '',
          }));
          return {
            items,
            page: pageableData?.page ?? page,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
      );
  }

  /**
   * Exporta el historial de acciones de un servicio a PDF
   * GET /api/export/pdf/history/services/{id}?title=...
   */
  exportHistoryPdf(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de servicios',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/pdf/history/services/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }

  /**
   * Exporta el historial de acciones de un servicio a Excel
   * GET /api/export/excel/history/services/{id}?title=...
   */
  exportHistoryExcel(
    id: string,
    options?: {
      title?: string;
    },
  ): Observable<Blob> {
    const params = new HttpParams().set(
      'title',
      options?.title || 'Historial de servicios',
    );

    return this.httpClient.get(
      `${this.apiConfig.apiUrl}/export/excel/history/services/${id}`,
      {
        params,
        responseType: 'blob',
      },
    );
  }
}
